import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { getHostProperties, getHostBookings, getHostPayouts } from '../../lib/api';
import StripeConnectCard from '../../components/StripeConnectCard';
import { 
  Building, 
  Calendar, 
  DollarSign, 
  TrendingUp,
  ArrowRight,
  Plus,
  Loader2
} from 'lucide-react';
import { format } from 'date-fns';

export default function HostDashboard() {
  const [stats, setStats] = useState({
    totalProperties: 0,
    activeListings: 0,
    pendingBookings: 0,
    totalEarnings: 0
  });
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [propertiesRes, bookingsRes, payoutsRes] = await Promise.all([
          getHostProperties({ limit: 50 }),
          getHostBookings({ limit: 5 }),
          getHostPayouts({ limit: 50 })
        ]);

        const properties = propertiesRes.data.items || [];
        const bookings = bookingsRes.data.items || [];
        const payouts = payoutsRes.data.items || [];

        setStats({
          totalProperties: properties.length,
          activeListings: properties.filter(p => p.status === 'approved' || p.status === 'published').length,
          pendingBookings: bookings.filter(b => b.status === 'pending').length,
          totalEarnings: payouts.reduce((sum, p) => sum + p.payout_amount, 0)
        });

        setRecentBookings(bookings);
      } catch (error) {
        console.error('Failed to load dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const formatPrice = (cents) => {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="host-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Host Dashboard</h1>
          <p className="text-slate-500 mt-1">Manage your properties and reservations</p>
        </div>
        <Link to="/app/host/properties/new">
          <Button className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Add Property
          </Button>
        </Link>
      </div>

      {/* Stripe Connect Status */}
      <StripeConnectCard />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Properties</p>
                <p className="text-3xl font-bold text-slate-900">{stats.totalProperties}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Building className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Active Listings</p>
                <p className="text-3xl font-bold text-slate-900">{stats.activeListings}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pending Bookings</p>
                <p className="text-3xl font-bold text-slate-900">{stats.pendingBookings}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Earnings</p>
                <p className="text-3xl font-bold text-slate-900">{formatPrice(stats.totalEarnings)}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-teal-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link to="/app/host/properties">
          <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
            <CardContent className="p-6 flex items-center gap-4">
              <Building className="w-8 h-8 text-primary" />
              <div>
                <h3 className="font-medium text-slate-900">My Properties</h3>
                <p className="text-sm text-slate-500">View and manage listings</p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-400 ml-auto" />
            </CardContent>
          </Card>
        </Link>

        <Link to="/app/host/reservations">
          <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
            <CardContent className="p-6 flex items-center gap-4">
              <Calendar className="w-8 h-8 text-primary" />
              <div>
                <h3 className="font-medium text-slate-900">Reservations</h3>
                <p className="text-sm text-slate-500">Review booking requests</p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-400 ml-auto" />
            </CardContent>
          </Card>
        </Link>

        <Link to="/app/host/payouts">
          <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
            <CardContent className="p-6 flex items-center gap-4">
              <DollarSign className="w-8 h-8 text-primary" />
              <div>
                <h3 className="font-medium text-slate-900">Payouts</h3>
                <p className="text-sm text-slate-500">View earnings history</p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-400 ml-auto" />
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Recent Bookings */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading">Recent Reservations</CardTitle>
          <Link to="/app/host/reservations">
            <Button variant="ghost" size="sm">
              View All
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {recentBookings.length > 0 ? (
            <div className="space-y-4">
              {recentBookings.map((booking) => (
                <div 
                  key={booking.id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                >
                  <div>
                    <h4 className="font-medium text-slate-900">{booking.property_title}</h4>
                    <p className="text-sm text-slate-500">
                      {booking.guest_name} • {format(new Date(booking.check_in), 'MMM d')} - {format(new Date(booking.check_out), 'MMM d')}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`
                      px-2 py-1 rounded-full text-xs font-medium
                      ${booking.status === 'paid' ? 'status-paid' : ''}
                      ${booking.status === 'pending' ? 'status-pending' : ''}
                      ${booking.status === 'approved' ? 'status-approved' : ''}
                    `}>
                      {booking.status}
                    </span>
                    <p className="text-sm font-medium text-slate-900 mt-1">
                      {formatPrice(booking.total_charged)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-slate-500 py-8">
              No reservations yet. Your bookings will appear here.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
